import React, { ReactNode } from 'react';

import { useAppSelector, useAppStore } from '@/lib/hooks';

import CourseCard from '@/common/components/course/display/CourseCard';

import { CourseEntity } from 'course-types';

interface Props {
  onSelect: (courseId: CourseEntity) => void;
  onRemove?: (courseId: CourseEntity) => void;
  className?: string;
  cardClassName?: string;
  gridClassName?: string;
  addCardPrompt?: string;
  addCardCondition?: boolean;
  addCardFunc?: () => void;
  groupName?: string;
  groupMemberCount?: number; // Total members in the group
  dropDown?: (course: CourseEntity) => ReactNode;
  cardSize?: 'small' | 'medium' | 'large'; // Control card size
  cardLayout?: 'responsive' | 'fixed-2-col'; // Control layout behavior
}

export default function CourseCards({
  onSelect,
  onRemove,
  addCardCondition,
  addCardFunc,
  addCardPrompt,
  className,
  groupMemberCount,
  cardClassName,
  gridClassName,
  cardSize = 'medium', // Default to medium size
  cardLayout = 'responsive', // Default to responsive layout
}: Props) {
  const store = useAppStore();
  const courseList: Array<CourseEntity> = useAppSelector(() => store.getState().course.courseList);

  // Define card size presets
  const sizePresets = {
    small: 'repeat(auto-fill, minmax(min(220px, 100%), 260px))',
    medium: 'repeat(auto-fill, minmax(min(240px, 100%), 265px))',
    large: 'repeat(auto-fill, minmax(min(280px, 100%), 350px))',
  };

  // Different grid class configurations based on layout type
  const gridClasses =
    cardLayout === 'fixed-2-col'
      ? 'grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5' // User Overview: 1 col mobile, 2 col tablet+
      : 'grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-5 lg:grid-cols-3 lg:gap-5'; // Main: responsive breakpoints

  return (
    <>
      <div
        className={`${gridClasses} self-stretch pb-6 ${className ?? ''} ${
          courseList && courseList.length === 0 ? 'justify-center' : ''
        }`}
        style={
          cardLayout === 'responsive'
            ? {
                ['--grid-template-cols' as any]: sizePresets[cardSize],
              }
            : undefined
        }
      >
        {cardLayout === 'responsive' && (
          <style
            dangerouslySetInnerHTML={{
              __html: `
            @media (min-width: 1280px) {
              [style*="--grid-template-cols"] {
                grid-template-columns: var(--grid-template-cols) !important;
              }
            }
          `,
            }}
          />
        )}
        {courseList &&
          courseList.map((course, index) => (
            <CourseCard
              key={index}
              title={course.name}
              id={course.id}
              courseCode={(course as any).course_code?.code}
              onClick={() => onSelect(course)}
              onRemove={onRemove ? () => onRemove(course) : undefined}
              participantCount={groupMemberCount} // Reuse total group member count for each course
              className={cardClassName}
              // TODO: Implement assignmentsToGrade once backend endpoint is available
              // This requires a new/updated backend endpoint to retrieve incoming due dates for assignments
              // assignmentsToGrade={(course as any).assignmentsToGrade || 0}

              // TODO: Implement updatedAt timestamp when backend supports it
              // updatedAt={(course as any).updatedAt || 'Updated today'}
            />
          ))}
        {(addCardCondition ?? true) && addCardFunc && addCardPrompt && (
          <div
            className='flex flex-col items-center justify-center opacity-100 border p-4 rounded-2xl border-dashed border border-border_gray_300 bg-surface_white cursor-pointer hover:border-border_gray_400 transition-all w-full gap-0'
            onClick={addCardFunc}
          >
            <div className='flex flex-col items-center justify-center gap-2'>
              {/* Book icon with light blue background */}
              <div className='w-[36] h-[36] opacity-100 gap-2 border p-2 rounded-lg bg-[color-mix(in_srgb,var(--color-brand-highlight)_13.6%,var(--surface-white))] border-[color-mix(in_srgb,var(--color-brand-highlight)_13.6%,var(--surface-white))] flex items-center justify-center shadow-[0px_1px_2px_0px_#0A0D120D]'>
                <svg
                  className='w-5 h-5 text-primary'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth={2}
                  viewBox='0 0 24 24'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253'
                  />
                </svg>
              </div>
              <div className='flex flex-col gap-0'>
                {/* Title */}
                <span className='text-text_gray_900 font-semibold text-sm leading-5 tracking-[0%] text-center'>
                  Add a course
                </span>

                {/* Description */}
                <span className='text-text_gray_600 font-normal text-xs leading-[18px] tracking-[0%] text-center'>
                  You can add a course by its code or name
                </span>
              </div>
            </div>

            {/* Add course button */}
            <button
              className='flex items-center justify-center gap-1.5 px-3.5 py-2 rounded-lg transition-all duration-200 w-full mt-1 bg-[color-mix(in_srgb,var(--color-brand-highlight)_13.6%,var(--surface-white))] hover:bg-primary hover:text-text_contrast shadow-[0px_1px_2px_0px_#0A0D120D] text-primary font-semibold text-sm leading-5 tracking-[0%]'
              onClick={(e) => {
                e.stopPropagation();
                if (addCardFunc) addCardFunc();
              }}
            >
              <svg
                className='w-4 h-4'
                fill='none'
                stroke='currentColor'
                strokeWidth={2}
                viewBox='0 0 24 24'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path strokeLinecap='round' strokeLinejoin='round' d='M12 4v16m8-8H4' />
              </svg>
              Add course
            </button>
          </div>
        )}
      </div>
    </>
  );
}
